/*
 * ==========================================================================
 * A/B FRAMEWORK — GA4 MEASUREMENT ENGINE
 * ==========================================================================
 * Generic: it measures whatever experiments ab-config.liquid declared. Adding
 * an experiment requires NO change to this file.
 *
 * Assignment already happened synchronously in <head> (ab-head.liquid), so
 * nothing here affects rendering.
 *
 * EVENTS, per experiment key:
 *   ab_<key>_view_<variant>    exposure  — page shows this experiment
 *   ab_<key>_click_<variant>   click     — visitor clicked a matching element
 *   ab_<key>_engaged_<variant> stayed    — 10s dwell, a click, or a real scroll
 *   ab_<key>_atc_<variant>     converted — added to cart after clicking this experiment
 *   ab_<key>_modal_<variant>   booking modal opened
 *
 * The variant is in the EVENT NAME, not only a parameter, because Windsor.ai's
 * GA4 connector reads `event_name` with zero setup but can only read custom
 * dimensions that are registered AND event-scoped (it exposes no user-scoped
 * dimensions at all). Suffixed names are readable the day data starts flowing.
 * The same values also ride along as params for optional custom dimensions.
 *
 * Exposure, not sessions, is the denominator: an experiment only counts a
 * visitor if its `sel` actually matched something on the page. Clicks-per-
 * exposure is the correct A/B rate and far less noisy than per-session.
 *
 * DELIVERY: straight to GA4 via gtag, deliberately bypassing GTM. Safe from
 * double-counting because GTM has no tags for ab_* event names. GTM injects
 * gtag.js at window.load + 1500ms, so events raised in the first few seconds
 * are queued (one queue, mirrored to sessionStorage) and flushed when gtag
 * appears; anything stranded by a navigation is replayed on the next page.
 *
 * Does NOT emit `cta_click` — that name belongs to assets/analytics-events.js
 * and must not be contaminated.
 *
 * Everything is best-effort and wrapped in try/catch. This must never be able
 * to break a booking.
 */
(function () {
  'use strict';

  var AB = window.KS_AB;

  /* ------------------------------------------------------------------------
   * EXCLUDED VISITOR (opt-out cookie, or the theme editor).
   * No tracking whatsoever — but still tidy the DOM down to the control
   * variant before returning.
   *
   * Why this matters: when nothing prunes, BOTH labels stay in the markup.
   * They are invisible (CSS hides variant B), so the page looks correct — but
   * textContent then reads "Book NowReserve Your Spot", and the mobile sticky
   * bar in content-tabs-meta.liquid copies a hero button's textContent
   * verbatim on DOMContentLoaded. An excluded visitor on mobile would get a
   * sticky button with both labels run together. Only affects excluded users,
   * i.e. exactly the people doing QA.
   * ---------------------------------------------------------------------- */
  if (!AB) {
    if (window.KS_AB_EXCLUDED) {
      try {
        var dead = document.querySelectorAll('[data-ks-ab-var]:not([data-ks-ab-var="a"])');
        for (var d = 0; d < dead.length; d++) {
          if (dead[d].parentNode) dead[d].parentNode.removeChild(dead[d]);
        }
      } catch (e) {}
    }
    return;
  }

  var KEYS = [];
  for (var k in AB) { if (Object.prototype.hasOwnProperty.call(AB, k)) KEYS.push(k); }
  if (!KEYS.length) return;

  window.dataLayer = window.dataLayer || [];

  /* =====================================================================
   * GA4 transport — ONE queue is the single source of truth; sessionStorage
   * is only a mirror of it, cleared atomically with it. (Keeping events in
   * two places and draining both is how you send everything twice.)
   * =================================================================== */
  var QUEUE = [];
  var STORE_KEY = 'ks_ab_pending';
  var MAX_QUEUE = 40;

  function readStored() {
    try {
      var raw = sessionStorage.getItem(STORE_KEY);
      var p = raw ? JSON.parse(raw) : [];
      return Object.prototype.toString.call(p) === '[object Array]' ? p : [];
    } catch (e) { return []; }
  }

  function writeStored(list) {
    try {
      if (!list || !list.length) sessionStorage.removeItem(STORE_KEY);
      else sessionStorage.setItem(STORE_KEY, JSON.stringify(list.slice(-MAX_QUEUE)));
    } catch (e) {}
  }

  function gtagReady() { return typeof window.gtag === 'function'; }

  function emit(name, params) {
    try { window.gtag('event', name, params); return true; } catch (e) { return false; }
  }

  /* Adopt anything a previous page stranded (click that navigated away before
   * gtag existed). Flagged replayed:true so it can be excluded if exact event
   * timing ever matters. */
  (function adoptStrays() {
    var strays = readStored();
    for (var i = 0; i < strays.length; i++) {
      if (strays[i] && strays[i][1]) strays[i][1].replayed = true;
      QUEUE.push(strays[i]);
    }
  })();

  function enqueue(name, params) {
    QUEUE.push([name, params]);
    if (QUEUE.length > MAX_QUEUE) QUEUE = QUEUE.slice(-MAX_QUEUE);
    writeStored(QUEUE);
  }

  function flush() {
    if (!gtagReady()) return false;
    if (!QUEUE.length) return true;
    var items = QUEUE;
    QUEUE = [];
    writeStored([]);   // clear BEFORE emitting so a re-entrant flush finds nothing
    for (var i = 0; i < items.length; i++) emit(items[i][0], items[i][1]);
    return true;
  }

  function track(expKey, action, extra) {
    try {
      var x = AB[expKey];
      if (!x) return;
      var name = 'ab_' + x.key + '_' + action + '_' + x.variant;
      var params = {
        experiment_id: x.id,
        experiment_key: x.key,
        experiment_variant: x.variant,
        experiment_mode: x.mode,
        experiment_type: x.type
      };
      if (extra) {
        for (var p in extra) {
          if (Object.prototype.hasOwnProperty.call(extra, p)) params[p] = extra[p];
        }
      }
      var dl = { event: name };
      for (var q in params) {
        if (Object.prototype.hasOwnProperty.call(params, q)) dl[q] = params[q];
      }
      window.dataLayer.push(dl);
      enqueue(name, params);
      flush();
    } catch (e) {}
  }

  (function waitForGtag() {
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      if (flush() || tries > 60) clearInterval(timer);   // 30s ceiling
    }, 500);
    window.addEventListener('load', function () { setTimeout(flush, 2000); });
  })();
  window.addEventListener('pagehide', function () { flush(); });

  /* =====================================================================
   * PUBLIC API — window.KS_AB_track(key, action, extra)
   * Lets any other script in the theme report an experiment event without
   * knowing anything about assignment or transport. Used by the popup, which
   * decides for itself when it is eligible to appear.
   *   window.KS_AB_track('popup', 'view')
   *   window.KS_AB_track('popup', 'minimize', { trigger: 'close_button' })
   * Safe to call unconditionally — a no-op if the experiment is not running
   * or the visitor is excluded.
   * =================================================================== */
  window.KS_AB_track = function (key, action, extra) {
    try { if (AB[key]) track(key, action, extra); } catch (e) {}
  };

  /* =====================================================================
   * HOOKS — behavioural experiments (type: 'hook')
   * Text tests swap words, CSS tests swap styling. A hook test swaps what a
   * click DOES while the element looks identical, so there is nothing to
   * paint and nothing to prune — no flicker by construction.
   *
   * Variant A is always "whatever the theme already does": the hook simply
   * does not run. Only variant B intercepts.
   *
   * ADDING A HOOK: add a function here keyed by experiment key. It receives
   * (event, element) and should call event.preventDefault() if it handles
   * the click. Return true if handled.
   * =================================================================== */
  var HOOKS = {

    /* cardcta — collection card button.
     * A: follow the href to the workshop listing page (theme default).
     * B: open Cowlendar straight from the card.
     *    Multi-variant products need the variant picker first; single-variant
     *    products POST /cart/add.js, which Cowlendar wraps and intercepts —
     *    the same mechanism location-featured-product-tabs.liquid already uses. */
    cardcta: function (evt, el) {
      var vid = el.getAttribute('data-ab-cardcta-variant');
      var pid = el.getAttribute('data-ab-cardcta-product');
      if (!vid && !pid) return false;
      evt.preventDefault();
      evt.stopPropagation();

      if (!vid && pid) {
        var trigger = document.querySelector('[data-modal-trigger="kraft-variant-selector-modal"][data-product-id="' + pid + '"]');
        if (trigger) { trigger.click(); return true; }
        window.location.href = el.getAttribute('href') || '/';   // fail open
        return true;
      }

      var span = el.querySelector('span');
      var original = span ? span.textContent : '';
      if (span) span.textContent = 'Loading...';
      el.style.pointerEvents = 'none';
      window.fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(vid, 10), quantity: 1 })
      })['catch'](function () {
        /* Cowlendar intercepts and runs its own flow */
      })['finally'](function () {
        if (span) span.textContent = original;
        el.style.pointerEvents = '';
      });
      return true;
    }

    /* ---- add further hooks here ---- */
  };

  /* =====================================================================
   * Text experiments: prune the losing label from the DOM.
   * CSS has already decided what painted, so this causes no flicker. It
   * guarantees a visitor can never see both labels even if some later
   * stylesheet overrides our display rules, and keeps the DOM clean for
   * screen readers and text scrapers. Critically it also runs BEFORE
   * DOMContentLoaded, so section scripts that copy a button's textContent
   * (e.g. the mobile sticky bar) can never read both labels concatenated.
   * =================================================================== */
  function pruneText(root) {
    try {
      var nodes = (root || document).querySelectorAll('[data-ks-ab-exp][data-ks-ab-var]');
      for (var i = 0; i < nodes.length; i++) {
        var n = nodes[i];
        var x = AB[n.getAttribute('data-ks-ab-exp')];
        if (!x) continue;
        if (n.getAttribute('data-ks-ab-var') !== x.variant && n.parentNode) {
          n.parentNode.removeChild(n);
        }
      }
    } catch (e) {}
  }

  function matches(root, sel) {
    if (!sel) return [];
    try { return (root || document).querySelectorAll(sel); } catch (e) { return []; }
  }

  /* Tag elements so click handling is cheap and debuggable. */
  function markTargets(root) {
    for (var i = 0; i < KEYS.length; i++) {
      var x = AB[KEYS[i]];
      var els = matches(root, x.sel);
      for (var j = 0; j < els.length; j++) {
        try {
          var host = els[j].closest('a, button, [data-modal-trigger]') || els[j];
          var cur = host.getAttribute('data-ks-ab-hit') || '';
          if (cur.indexOf(x.key) === -1) {
            host.setAttribute('data-ks-ab-hit', (cur ? cur + ' ' : '') + x.key);
          }
        } catch (e) {}
      }
    }
  }

  /* =====================================================================
   * Exposure — once per experiment per page load, and only if its selector
   * actually matched. An experiment nobody saw must not dilute its own rate.
   * =================================================================== */
  var exposed = {};
  function sendExposures() {
    for (var i = 0; i < KEYS.length; i++) {
      var key = KEYS[i];
      if (exposed[key]) continue;
      var x = AB[key];
      var n = matches(document, x.sel).length;
      if (!n) continue;
      exposed[key] = true;
      track(key, 'view', {
        match_count: n,
        page_path: window.location.pathname,
        page_type: window.__kraft_page_category || ''
      });
    }
    if (Object.keys(exposed).length) armEngagement();
  }


  /* =====================================================================
   * ENGAGEMENT — a bounce rate you can actually read per arm.
   *
   *   ab_<key>_view_<v>      the visitor was shown the experiment
   *   ab_<key>_engaged_<v>   ...and then stayed
   *
   * 1 − engaged/view IS bounce rate, split by variant. GA4's own bounce
   * metric cannot answer this: it is session-scoped and carries no variant,
   * and Windsor exposes no user-scoped dimension to join a variant onto. Any
   * experiment whose whole point is "does this make people leave" — hero
   * reveal, popup timing, page speed — is unreadable without this event.
   *
   * The trigger mirrors GA4's engaged-session rule as closely as a page-scoped
   * script can: 10 seconds on the page, OR a click, OR a real scroll. First
   * one wins, and it fires at most once per experiment per page load, so the
   * denominator (views) and the numerator (engagements) are the same unit.
   *
   * A scroll of under 150px is ignored: phones fire scroll events during
   * layout settling and from momentum after the page is abandoned, and
   * counting those as engagement would flatten the very difference the hero
   * test exists to detect.
   * =================================================================== */
  var ENGAGED_MS = 10000;
  var ENGAGED_SCROLL_PX = 150;
  var PAGE_START = Date.now();
  var engagedSent = {};
  var engagementArmed = false;

  function markEngaged(reason) {
    for (var i = 0; i < KEYS.length; i++) {
      var key = KEYS[i];
      if (!exposed[key] || engagedSent[key]) continue;
      engagedSent[key] = true;
      track(key, 'engaged', {
        engagement_trigger: reason,
        engagement_ms: Date.now() - PAGE_START,
        page_path: window.location.pathname,
        page_type: window.__kraft_page_category || ''
      });
    }
  }

  function armEngagement() {
    if (engagementArmed) return;
    engagementArmed = true;

    var startY = window.pageYOffset || document.documentElement.scrollTop || 0;

    function stopListening() {
      try {
        window.removeEventListener('scroll', onScroll, true);
        window.removeEventListener('click', onClick, true);
      } catch (e) {}
    }
    function onScroll() {
      var y = window.pageYOffset || document.documentElement.scrollTop || 0;
      if (Math.abs(y - startY) < ENGAGED_SCROLL_PX) return;
      stopListening();
      markEngaged('scroll');
    }
    function onClick() {
      stopListening();
      markEngaged('click');
    }

    setTimeout(function () { markEngaged('dwell_10s'); }, ENGAGED_MS);

    try {
      window.addEventListener('scroll', onScroll, { passive: true, capture: true });
      window.addEventListener('click', onClick, { passive: true, capture: true });
    } catch (e) {
      window.addEventListener('scroll', onScroll, true);
      window.addEventListener('click', onClick, true);
    }
  }



  /* =====================================================================
   * STICKY — variant B needs its own scroll controller, and here is why.
   *
   * content-tabs-meta.liquid ships one already. On the workshop templates it
   * can never fire, for two independent reasons:
   *
   *   1. It early-returns unless it finds a buttons wrapper inside its own
   *      section. On this template there is none, so it bails before binding.
   *   2. Even if it bound, its condition is unsatisfiable here. It asks for
   *      scrollY > max(700, viewportHeight * 1.5) AND viewportBottom <
   *      anchorTop - 4. With a 890px viewport that is scrollY > 1335 AND
   *      scrollY < 706 — an empty range. The anchor sits 1600px down, above
   *      the threshold meant to clear the hero.
   *
   * So the bar is rendered, wired, and permanently retracted — on mobile too,
   * where it is supposed to be the whole point. That is a real bug on the
   * busiest template on the site, but fixing it in the section would change
   * what every mobile visitor sees, and mobile is not in this test's
   * denominator. Changing the control group's experience mid-experiment to fix
   * something the experiment is measuring is how you get a result you cannot
   * trust. So: this controller is scoped to variant B on desktop, the section
   * keeps its own behaviour untouched, and the mobile bug is reported rather
   * than silently patched.
   *
   * The trigger is the honest one: show the bar exactly when no booking CTA is
   * on screen. Retract near the footer so it never covers it.
   * =================================================================== */
  function stickyController() {
    try {
      var x = AB.sticky;
      if (!x || x.variant !== 'b') return;
      if (!document.documentElement.classList.contains('ks-vw-desktop')) return;

      var bar = document.querySelector('.mobile_booking_cta');
      if (!bar) return;

      var heroCta = document.querySelector('.hero-cta-buttons-wrapper')
                 || document.querySelector('[id^="hero_section"] .jx-btn--orange, [id^="hero_section"] .btn-slide');
      var footer = document.querySelector('#footer_area, footer');
      var isIn = false;

      function shouldShow() {
        var vh = window.innerHeight || 700;
        /* Never compete with a CTA the visitor can already see. */
        if (heroCta) {
          var h = heroCta.getBoundingClientRect();
          if (h.bottom > 0) return false;
        } else if ((window.pageYOffset || 0) < vh * 0.8) {
          return false;
        }
        if (footer) {
          var f = footer.getBoundingClientRect();
          if (f.top < vh) return false;          /* footer in view — stand down */
        }
        if (document.body.classList.contains('nav-open') ||
            document.body.classList.contains('mega-menu-open')) return false;
        return true;
      }

      function update() {
        var want = shouldShow();
        if (want === isIn) return;
        isIn = want;
        bar.classList.toggle('is-fixed', want);
        bar.classList.toggle('is-normal', !want);
      }

      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      update();
      /* The hero image and the tab content both settle after first paint and
         move the footer; re-measure once things have stopped moving. */
      window.addEventListener('load', update);
      setTimeout(update, 1200);
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', stickyController, { once: true });
  } else {
    stickyController();
  }

  /* =====================================================================
   * LOCTAB — the home location tabs remember the visitor (variant B only).
   *
   * Two halves, both gated on variant B so arm A's experience is untouched:
   *   RECORD — on any page whose location is recognisable (the hero's
   *            #kraft-page-location-context span, else URL patterns), store
   *            a normalised token in localStorage.
   *   APPLY  — on a page rendering location-bestsellers-tabs, if a stored
   *            token matches a tab, activate that tab instead of the default.
   *
   * The switch is done by toggling the same classes the theme's own tab
   * handler toggles — deliberately NOT by calling .click() on the button,
   * because a synthetic click would bubble through the section and count as
   * an ab_loctab_click_b on every remembering pageload, corrupting the
   * click metric with self-inflicted clicks.
   * =================================================================== */
  function loctabController() {
    try {
      var x = AB.loctab;
      if (!x || x.variant !== 'b') return;

      var LS_KEY = 'ks_ab_lastloc';

      function locToken() {
        var s = '';
        try {
          var ctx = document.getElementById('kraft-page-location-context');
          s = ctx ? (ctx.getAttribute('data-location') || '') : '';
        } catch (e) {}
        var hay = (s + ' ' + (window.location.pathname || '')).toLowerCase();
        if (/fidi|financial-district/.test(hay)) return 'fidi';
        if (/honolulu|hawaii/.test(hay)) return 'honolulu';
        if (/midtown|nyc|new-york|new york/.test(hay)) return 'new york';
        if (/san-francisco|san francisco|soma|bay-area/.test(hay)) return 'san francisco';
        if (/san-jose|san jose/.test(hay)) return 'san jose';
        if (/los-angeles|los angeles/.test(hay)) return 'los angeles';
        if (/irvine/.test(hay)) return 'irvine';
        if (/virtual/.test(hay)) return 'virtual';
        return '';
      }

      /* APPLY first: the current page's own token must not overwrite the
         one we are about to act on (the home page has no token, but the
         section also renders on some location-flavoured pages). */
      var container = document.querySelector('.location-bestsellers-tabs-container');
      if (container) {
        var stored = '';
        try { stored = window.localStorage.getItem(LS_KEY) || ''; } catch (e) {}
        if (stored) {
          var nav = container.querySelector('.content-tabs-nav');
          var btns = nav ? nav.querySelectorAll('.content-tabs-nav-item') : [];
          var target = null;
          for (var i = 0; i < btns.length; i++) {
            var label = (btns[i].getAttribute('data-location-label') || btns[i].textContent || '').toLowerCase();
            if (label && label.indexOf(stored) !== -1) { target = btns[i]; break; }
          }
          if (target && !target.classList.contains('active')) {
            for (var j = 0; j < btns.length; j++) {
              btns[j].classList.remove('active');
              btns[j].setAttribute('aria-selected', 'false');
            }
            target.classList.add('active');
            target.setAttribute('aria-selected', 'true');
            var panelId = target.getAttribute('data-tab');
            var panels = container.querySelectorAll('.content-tabs-panel');
            for (var m = 0; m < panels.length; m++) {
              panels[m].classList.toggle('active', panels[m].id === panelId);
            }
            /* Recorded as its own action, not a click: how often memory
               actually re-aimed the section. Denominator is ab_loctab_view_b. */
            track('loctab', 'applied', { applied_location: stored });
          }
        }
      }

      var token = locToken();
      if (token) {
        try { window.localStorage.setItem(LS_KEY, token); } catch (e) {}
      }
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loctabController, { once: true });
  } else {
    loctabController();
  }

  /* =====================================================================
   * CONVERSION — ab_<key>_atc_<variant>
   *
   * Click-through rate cannot read every experiment. The collection-card test
   * is the clearest case: both arms render a byte-identical button, so CTR
   * measures the button, not the destination, and the two arms are guaranteed
   * to tie. What actually differs is whether the visitor ends up adding a
   * workshop to the cart — and in variant A that happens on a DIFFERENT PAGE,
   * one navigation later.
   *
   * So the click writes an intent marker to sessionStorage, and the marker
   * survives the navigation. When an add_to_cart is observed on any subsequent
   * page within the window, every open intent converts and is cleared.
   *
   * add_to_cart is read off the dataLayer rather than by patching fetch. The
   * theme already raises it from one place (analytics-events.js) for every path
   * into the cart — button, drawer, Cowlendar, quick-add — so listening there
   * gets all of them for free, and, critically, patching window.fetch is the
   * one thing in this theme that would break Cowlendar's booking interception.
   * An analytics experiment must never be able to break a booking.
   * =================================================================== */
  var INTENT_KEY = 'ks_ab_intent';
  var INTENT_TTL = 30 * 60 * 1000;   /* 30 min — a GA4 session */

  function readIntents() {
    try {
      var raw = sessionStorage.getItem(INTENT_KEY);
      var list = raw ? JSON.parse(raw) : [];
      if (!list || !list.length) return [];
      var now = Date.now();
      return list.filter(function (i) { return i && i.t && (now - i.t) < INTENT_TTL; });
    } catch (e) { return []; }
  }

  function writeIntents(list) {
    try { sessionStorage.setItem(INTENT_KEY, JSON.stringify(list.slice(-12))); } catch (e) {}
  }

  function recordIntent(key) {
    try {
      var x = AB[key];
      if (!x) return;
      var list = readIntents().filter(function (i) { return i.k !== key; });
      list.push({ k: key, v: x.variant, t: Date.now(), p: window.location.pathname });
      writeIntents(list);
    } catch (e) {}
  }

  function convertIntents(reason) {
    try {
      var list = readIntents();
      if (!list.length) return;
      writeIntents([]);            /* clear BEFORE emitting, so a re-entrant
                                      dataLayer push cannot double-count */
      for (var i = 0; i < list.length; i++) {
        var it = list[i];
        if (!AB[it.k]) continue;
        /* Emitted against the variant recorded AT CLICK TIME, not the current
           assignment. They are the same in every normal case; they diverge if
           the visitor cleared cookies mid-session, and in that case the click
           and the conversion belong to the arm that earned them. */
        var x = AB[it.k];
        var saved = x.variant;
        x.variant = it.v;
        track(it.k, 'atc', {
          conversion_trigger: reason,
          seconds_since_click: Math.round((Date.now() - it.t) / 1000),
          click_page: it.p || '',
          page_path: window.location.pathname
        });
        x.variant = saved;
      }
    } catch (e) {}
  }

  (function watchDataLayer() {
    try {
      window.dataLayer = window.dataLayer || [];
      var origPush = window.dataLayer.push;
      window.dataLayer.push = function () {
        var out;
        try { out = origPush.apply(window.dataLayer, arguments); }
        catch (e) { out = Array.prototype.push.apply(window.dataLayer, arguments); }
        try {
          for (var i = 0; i < arguments.length; i++) {
            var ev = arguments[i] && arguments[i].event;
            /* ab_* is our own traffic — listening to it would recurse. */
            if (typeof ev === 'string' && ev.indexOf('ab_') !== 0 && ev === 'add_to_cart') {
              convertIntents('add_to_cart');
            }
          }
        } catch (e) {}
        return out;
      };
    } catch (e) {}
  })();

  /* =====================================================================
   * Clicks — delegated, so late/AJAX-injected elements are covered. One
   * click can legitimately count for several experiments at once (e.g. an
   * orange booking button is in both the copy and colour tests).
   * =================================================================== */
  var lastClick = {};
  document.addEventListener('click', function (evt) {
    try {
      var t = evt.target;
      if (!t || !t.closest) return;
      var now = Date.now();

      for (var i = 0; i < KEYS.length; i++) {
        var key = KEYS[i];
        var x = AB[key];
        if (!x.sel) continue;
        var hit = null;
        try { hit = t.closest(x.sel); } catch (e) { continue; }
        if (!hit) continue;

        if (now - (lastClick[key] || 0) < 250) continue;   // guard nested handlers
        lastClick[key] = now;

        var host = hit.closest('a, button, [data-modal-trigger]') || hit;

        /* Behavioural override for variant B. Tracked either way, so the
         * click counts for both arms and the CTR stays comparable. */
        if (x.type === 'hook' && x.variant === 'b' && HOOKS[key]) {
          try { HOOKS[key](evt, host); } catch (e) {}
        }

        recordIntent(key);

        track(key, 'click', {
          cta_text: (host.textContent || '').trim().substring(0, 100),
          cta_modal: host.getAttribute('data-modal-trigger') || '',
          cta_url: host.getAttribute('href') || '',
          page_path: window.location.pathname,
          page_type: window.__kraft_page_category || ''
        });
      }
      tagCart();
    } catch (e) {}
  }, true);

  /* =====================================================================
   * Booking modal opens — mid-funnel checkpoint between click and order.
   * Attributed to every active experiment.
   * =================================================================== */
  try {
    var seen = {};
    new MutationObserver(function (recs) {
      for (var i = 0; i < recs.length; i++) {
        var node = recs[i].target;
        if (!node || node.nodeType !== 1) continue;
        var id = node.id || '';
        if (id.indexOf('kraft-') !== 0 && id.indexOf('-modal') === -1) continue;
        var open = node.classList.contains('is-open') ||
                   node.classList.contains('active') ||
                   node.getAttribute('aria-hidden') === 'false';
        if (open && !seen[id]) {
          seen[id] = true;
          for (var j = 0; j < KEYS.length; j++) {
            if (exposed[KEYS[j]]) track(KEYS[j], 'modal', { modal_id: id });
          }
        } else if (!open) { seen[id] = false; }
      }
    }).observe(document.documentElement, {
      subtree: true, attributes: true, attributeFilter: ['class', 'aria-hidden']
    });
  } catch (e) {}

  /* =====================================================================
   * Shopify cart attributes — order-level attribution that owes nothing to
   * GA4, ad blockers or cross-domain tracking. Lands on the order, so you can
   * segment bookings by variant straight from an order export. This is the
   * number to trust for the revenue guardrail.
   *
   * Writes one attribute per experiment: "AB copy" = "A", "AB color" = "B".
   * Never called for visitors with no cart — creating a cart for every visitor
   * would pollute Klaviyo abandoned-cart flows.
   * =================================================================== */
  function hasCart() {
    try { return /(?:^|;\s*)cart=/.test(document.cookie); } catch (e) { return false; }
  }

  function variantFingerprint() {
    var out = [];
    for (var i = 0; i < KEYS.length; i++) out.push(KEYS[i] + ':' + AB[KEYS[i]].variant);
    return out.join('|');
  }

  function tagCart() {
    try {
      var fp = variantFingerprint();
      if (sessionStorage.getItem('ks_ab_cart_tagged') === fp) return;
      if (!hasCart()) return;
      var attrs = {};
      for (var i = 0; i < KEYS.length; i++) {
        var x = AB[KEYS[i]];
        attrs['AB ' + x.key] = x.variant.toUpperCase();
      }
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      }).then(function () {
        try { sessionStorage.setItem('ks_ab_cart_tagged', fp); } catch (e) {}
      }).catch(function () {});
    } catch (e) {}
  }

  /* =====================================================================
   * Boot. Prune runs immediately (before DOMContentLoaded) on purpose.
   * =================================================================== */
  markTargets(document);
  pruneText(document);

  function boot() {
    markTargets(document);
    pruneText(document);
    sendExposures();
    if (hasCart()) tagCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    markTargets(e.target); pruneText(e.target);
  });
  window.addEventListener('load', boot);
})();
